import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Button from '../Button';
import CustomPrintConfiguratorPhotoSelectorModal from './components/CustomPrintConfiguratorPhotoSelectorModal';
import Alert from '../Alert';
import { getErrorString } from '../../../util/normalize-errors';
import Spinner from '../Spinner';
import {
  getAlbumCustomPrintName,
  getAlbumCustomPrintPreviewImageUrl,
  albumCustomPrintRequiresAddressToConfig,
  customPrintCaptionEnabled,
} from '../../../util/custom-prints';
import PhotoViewModal from '../modal/PhotoViewModal';
import { PHOTO_VIEW_MODAL } from '../../../constants/modals';
import { CUSTOM_PRINT_TYPE } from '../../../constants/pro-custom-prints';
import CustomPrintGenericTemplate from './CustomPrintGenericTemplate';
import CollageConfiguratorTemplate from './CollageConfiguratorTemplate';
import MemoryMateConfiguratorTemplate from './MemoryMateConfiguratorTemplate';
import { CustomPrintAddressSelectModal } from './components/CustomPrintAddressSelect';
import UserAddressDisplayEditBox from '../user-address/UserAddressDisplayEditBox';
import CustomPrintConfigurationSuccess from './components/CustomPrintConfigurationSuccess';
import {
  isNativeAppWebView,
  postNativeAppCustomConfiguratorError,
} from '../../../services/native-bridge/native-apps-interface';
import { isMobile } from '../../../util/device-detector';
import InfoCircleIcon from '../../icons/InfoCircleIcon';
import DismissiblePersistent from '../DismissiblePersistent';
import { CUSTOM_PRINT_IMAGE_TIP } from '../../../constants/dismissibleKeys';

class CustomPrintConfigurator extends Component {
  static propTypes = {
    loading: PropTypes.bool,
    // Optional account data in case we want to
    // create account/shipping addresses client side
    account: PropTypes.shape({
      id: PropTypes.string,
      addresses: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
        }),
      ),
    }),
    albumCustomPrint: PropTypes.shape({
      id: PropTypes.string.isRequired,
      photos_no: PropTypes.number.isRequired,
      name: PropTypes.string,
      custom_print: PropTypes.shape({
        custom_print_caption_enabled: PropTypes.bool,
        type: PropTypes.oneOf(Object.values(CUSTOM_PRINT_TYPE)),
      }),
      selected_identity: PropTypes.shape({
        id: PropTypes.string,
      }),
      identity: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
      custom_print_caption_enabled: PropTypes.bool,
    }),
    candidatePhotoGroups: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        total: PropTypes.number,
        photos: PropTypes.array,
        loadMore: PropTypes.func,
      }),
    ),
    accountAddressCreate: PropTypes.func,
    completed: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    onSubmitSuccess: PropTypes.func,
    openModal: PropTypes.func,
    className: PropTypes.string,
    onSkip: PropTypes.func,
    renderSuccess: PropTypes.func,
    renderSubmitButton: PropTypes.func,
    renderHeader: PropTypes.func,
  };

  state = {
    /**
     * [{
     *   position: Number,
     *   photo: Object,
     *   cropbox: Object,
     * }].
     */
    selectedPhotos: [],
    caption: this.props.albumCustomPrint?.identity?.name,
    selectedPhotoPosition: null,
    shippingAddress: undefined,
    addressModalOpen: false,
    error: '',
    submitting: false,
    success: false,
  };

  componentDidMount() {
    const { albumCustomPrint } = this.props;
    const { caption } = this.state;
    const name = albumCustomPrint?.identity?.name;
    const captionEnabled = customPrintCaptionEnabled(albumCustomPrint);

    if (name && !caption && captionEnabled) {
      this.setState(() => ({ caption: name }));
    }

    if (albumCustomPrint) {
      this.maybeRequestShippingAddress(albumCustomPrint);
    }
  }

  componentDidUpdate(prevProps) {
    const { albumCustomPrint: prevAlbumCustomPrint } = prevProps;
    const { albumCustomPrint } = this.props;

    if (albumCustomPrint && !prevAlbumCustomPrint) {
      this.maybeRequestShippingAddress(albumCustomPrint);
    }
  }

  maybeRequestShippingAddress = albumCustomPrint => {
    const { shippingAddress, addressModalOpen } = this.state;

    if (
      !shippingAddress &&
      !addressModalOpen &&
      albumCustomPrintRequiresAddressToConfig(albumCustomPrint)
    ) {
      this.setState(() => ({
        addressModalOpen: true,
      }));
    }
  };

  onTriggerPhotoSelection = position => {
    this.setState(({ selectedPhotos }) => {
      const selectedPhoto = selectedPhotos.find(
        photo => photo.position === position,
      );

      if (selectedPhoto) {
        return {
          selectedPhotoPosition: position,
        };
      }

      return {
        selectedPhotoPosition: position,
        selectedPhotos: [
          ...selectedPhotos,
          {
            position,
            photo: null,
          },
        ],
      };
    });
  };

  onPhotoSelect = (photo, position) => {
    this.setState(({ selectedPhotos }) => ({
      error: '',
      selectedPhotoPosition: null,
      selectedPhotos: selectedPhotos.map(selectedPhoto => {
        if (selectedPhoto.position === position) {
          return {
            ...selectedPhoto,
            photo,
          };
        }

        return selectedPhoto;
      }),
    }));
  };

  onPhotoCropboxSet = (position, cropbox) => {
    this.setState(({ selectedPhotos }) => ({
      selectedPhotos: selectedPhotos.map(selectedPhoto => {
        if (selectedPhoto.position === position) {
          return {
            ...selectedPhoto,
            cropbox,
          };
        }

        return selectedPhoto;
      }),
    }));
  };

  onCaptionChange = event => {
    const { value, name } = event.target;

    this.setState(() => ({
      [name]: value,
    }));
  };

  onConfigFinish = () => {
    const { albumCustomPrint, onSubmit, onSubmitSuccess } = this.props;
    const { selectedPhotos, caption, shippingAddress } = this.state;

    // Validate photo selections
    if (!this.validateCustomPrintPhotoSelections()) {
      const message = 'Please select a photo for all positions.';

      this.setState(() => ({
        error: message,
        submitting: false,
      }));
      postNativeAppCustomConfiguratorError(message);

      return;
    }

    if (!this.validateCustomPrintCaption()) {
      const message = 'Please type your desired caption.';

      this.setState(() => ({
        error: message,
        submitting: false,
      }));
      postNativeAppCustomConfiguratorError(message);

      return;
    }

    const captionEnabled = customPrintCaptionEnabled(albumCustomPrint);

    this.setState(
      () => ({
        submitting: true,
        error: '',
      }),
      async () => {
        try {
          const response = await onSubmit({
            albumCustomPrint,
            caption: captionEnabled ? caption : '',
            photos: selectedPhotos.map(({ photo, position, cropbox }) => ({
              ...(photo.custom
                ? { asset_image_id: photo.id }
                : { photo_id: photo.id }),
              position,
              cropbox,
            })),
            address: shippingAddress,
          });

          if (onSubmitSuccess) {
            this.setState(
              () => ({ submitting: false }),
              () => {
                onSubmitSuccess(response, albumCustomPrint);
              },
            );
          } else {
            this.setState(() => ({ success: true, submitting: false }));
          }
        } catch (error) {
          const message = getErrorString(error);
          this.setState(
            () => ({
              error: message,
              submitting: false,
            }),
            () => {
              postNativeAppCustomConfiguratorError(message);
            },
          );
        }
      },
    );
  };

  validateCustomPrintPhotoSelections = () => {
    const { selectedPhotos } = this.state;
    const { albumCustomPrint } = this.props;

    // Make sure all selected photos are properly configured
    if (
      selectedPhotos.some(({ photo, position }) => !photo || position == null)
    ) {
      return false;
    }

    // Make sure all photos are selected.
    if (albumCustomPrint.photos_no !== selectedPhotos.length) {
      return false;
    }

    return true;
  };

  validateCustomPrintCaption = () => {
    const { caption } = this.state;
    const { albumCustomPrint } = this.props;

    if (albumCustomPrint.custom_print_caption_enabled && !caption) {
      return false;
    }

    return true;
  };

  render() {
    const {
      loading,
      albumCustomPrint,
      completed,
      openModal,
      className,
      onSkip,
      candidatePhotoGroups,
      renderSuccess,
      renderSubmitButton,
      renderHeader,
      accountAddressCreate,
      account,
    } = this.props;
    const {
      selectedPhotos,
      caption,
      selectedPhotoPosition,
      submitting,
      error,
      success,
      shippingAddress,
      addressModalOpen,
    } = this.state;

    if (loading || !albumCustomPrint) {
      return (
        <div className="text-center my-3">
          <Spinner />
        </div>
      );
    }

    if (completed || success) {
      return renderSuccess ? (
        renderSuccess(albumCustomPrint)
      ) : (
        <CustomPrintConfigurationSuccess />
      );
    }

    const selectedPhoto = selectedPhotos.find(
      ({ position }) => position === selectedPhotoPosition,
    );
    const previewImageUrl =
      getAlbumCustomPrintPreviewImageUrl(albumCustomPrint);
    const [address] =
      albumCustomPrint?.account?.addresses ?? account?.addresses ?? [];
    const classes = classNames({
      'custom-print-config-wrap': true,
      [className]: !!className,
    });

    return (
      <Fragment>
        <div className={classes}>
          {renderHeader ? (
            renderHeader(albumCustomPrint)
          ) : (
            <div className="custom-print-config-header mb-1">
              <div className="custom-print-config-header-wrap mb-1">
                <p className="custom-print-config-title">
                  Configure your {getAlbumCustomPrintName(albumCustomPrint)}
                </p>

                {previewImageUrl && openModal && (
                  <Button
                    btnStyle="link"
                    className="p-0 font-weight-bold flex-none"
                    onClick={() => openModal(PHOTO_VIEW_MODAL)}
                  >
                    See a sample
                  </Button>
                )}
              </div>

              <p className="text-sm text-italic">
                Select a photo for each position and press the button below to
                complete the configuration.
              </p>
            </div>
          )}

          {(albumCustomPrint.custom_print.type ===
            CUSTOM_PRINT_TYPE.CUSTOM_PRINT ||
            albumCustomPrint.custom_print.type == null) && (
            <CustomPrintGenericTemplate
              albumCustomPrint={albumCustomPrint}
              config={{
                caption,
                photos: selectedPhotos,
              }}
              onCaptionChange={this.onCaptionChange}
              onTriggerPhotoSelection={this.onTriggerPhotoSelection}
            />
          )}
          {albumCustomPrint.custom_print.type === CUSTOM_PRINT_TYPE.COLLAGE && (
            <Fragment>
              {albumCustomPrint.photos_no !== 2 && (
                <CollageConfiguratorTemplate
                  albumCustomPrint={albumCustomPrint}
                  config={{
                    caption,
                    photos: selectedPhotos,
                  }}
                  onTriggerPhotoSelection={this.onTriggerPhotoSelection}
                  onPhotoCropboxSet={this.onPhotoCropboxSet}
                />
              )}

              {albumCustomPrint.photos_no === 2 && (
                <MemoryMateConfiguratorTemplate
                  albumCustomPrint={albumCustomPrint}
                  config={{
                    caption,
                    photos: selectedPhotos,
                  }}
                  onTriggerPhotoSelection={this.onTriggerPhotoSelection}
                  onPhotoCropboxSet={this.onPhotoCropboxSet}
                />
              )}
              {selectedPhotos.length > 0 && (
                <DismissiblePersistent storeKey={CUSTOM_PRINT_IMAGE_TIP}>
                  {(onDismiss, visible) =>
                    visible && (
                      <div className="custom-print-config-pan-notice">
                        <div className="custom-print-config-pan-notice-content">
                          <InfoCircleIcon
                            size="36"
                            color="#0f84fe"
                            className="mb-1"
                          />
                          <p>
                            {isMobile()
                              ? 'Zoom by pinching inside each frame and drag the image around with your finger to position it.'
                              : 'Scroll to zoom inside each frame and move the image around to position it.'}
                          </p>

                          <Button onClick={onDismiss}>Got it!</Button>
                        </div>
                      </div>
                    )
                  }
                </DismissiblePersistent>
              )}
            </Fragment>
          )}

          <CustomPrintAddressSelectModal
            address={address}
            userId={account && account.id}
            createAccountAddress={accountAddressCreate}
            onAddressConfirm={newAddress =>
              this.setState(() => ({
                shippingAddress: newAddress,
                addressModalOpen: false,
              }))
            }
            forceOpen={addressModalOpen}
            onSkip={onSkip}
          />

          {shippingAddress && (
            <UserAddressDisplayEditBox
              title="Delivered to:"
              address={shippingAddress}
              className="my-2"
              addressClassName="user-address-alt user-address-white"
              onClick={() =>
                this.setState(() => ({
                  addressModalOpen: true,
                }))
              }
            />
          )}

          {error && !isNativeAppWebView() && (
            <Alert type="danger">{error}</Alert>
          )}

          {renderSubmitButton ? (
            renderSubmitButton({
              albumCustomPrint,
              submitting,
              disabled:
                submitting ||
                loading ||
                !this.validateCustomPrintPhotoSelections() ||
                !this.validateCustomPrintCaption(),
              onSubmit: this.onConfigFinish,
            })
          ) : (
            <Button
              className="btn-custom-print-config-submit mt-2"
              btnSize="lg"
              btnBlock
              onClick={this.onConfigFinish}
              disabled={submitting || loading}
            >
              {submitting ? 'Loading…' : 'Done selecting photos'}
            </Button>
          )}
        </div>

        <CustomPrintConfiguratorPhotoSelectorModal
          defaultPhotoGroups={candidatePhotoGroups}
          onPhotoConfirm={this.onPhotoSelect}
          selectedPhoto={selectedPhoto}
          forceOpen={!!selectedPhoto}
          onClose={() => {
            this.setState(() => ({ selectedPhotoPosition: null }));
          }}
          customUploadEnabled
        />

        {previewImageUrl && openModal && (
          <PhotoViewModal photoUrl={previewImageUrl} />
        )}
      </Fragment>
    );
  }
}

export default CustomPrintConfigurator;
